export type InvitePlatform = "meta" | "youtube" | "tiktok" | "spotify";

export type InviteIntegrationSource = "podscope" | "prixi";

export interface InviteData {
  id: string;
  role: "user" | "admin";
  status: "active" | "used" | "expired";
  createdAt: string;
  expiresAt: string;
  createdBy: string;
  companyName?: string;
  companySlug?: string;
  inviteeEmail?: string;
  podcastTitle?: string;
  podcastId?: string;
  socialAccountId?: string;
  platform?: InvitePlatform;
  companyImageS3Key?: string;
  companyImageUrl?: string;
  integrationSource?: InviteIntegrationSource;
  prixiPodcastId?: string;
  linkedAccount?: {
    accountId?: string;
    username?: string;
    profileUrl?: string;
  };
  companyPodcasts?: CompanyPodcastOption[];
  targetPodcast?: {
    podcastId: string;
    title: string;
    image?: string;
    igHandle?: string;
  };
  metaInviteMultiPodcastEnabled?: boolean;
}

export type CompanyPodcastOption = {
  podcastId: string;
  title: string;
  image?: string;
  igHandle?: string;
  hasMetaConnection: boolean;
};

export type MetaConnectStep = "select" | "mapping";

export type InviteUnavailableReason =
  | "expired"
  | "revoked"
  | "already_used"
  | "network"
  | "not_found"
  | "unknown";

export type InviteViewPhase =
  | "loading"
  | "revalidating"
  | "fatal"
  | "expired"
  | "unavailable"
  | "connect"
  | "connect_error"
  | "meta_page_id"
  | "meta_podcast_select"
  | "meta_mapping"
  | "connecting"
  | "success"
  | "already_connected"
  | "disconnected"
  | "prixi_connect"
  | "prixi_connect_error"
  | "prixi_meta_page_id"
  | "prixi_meta_podcast_select"
  | "prixi_meta_mapping"
  | "prixi_connecting"
  | "prixi_success"
  | "prixi_already_connected"
  | "prixi_disconnected"
  | "prixi_expired"
  | "prixi_unavailable";

export interface InviteUrlParams {
  successParam: string | null;
  disconnectedParam: string | null;
  errorParam: string | null;
  verifiedParam: string | null;
}

export interface InviteInFlightFlags {
  facebookLoading: boolean;
  metaSubmitting: boolean;
  spotifyLoading: boolean;
  disconnectLoading: boolean;
}

export interface LinkedAccountDisplay {
  platform: InvitePlatform;
  accountId?: string;
  username?: string;
  profileUrl?: string;
}

export interface DeriveInviteViewPhaseInput {
  loading: boolean;
  revalidating: boolean;
  error: string | null;
  unavailableReason: InviteUnavailableReason | null;
  inviteExpired: boolean;
  socialAccountId: string | null;
  hasInviteData: boolean;
  urlParams: InviteUrlParams;
  metaNeedsPageId: boolean;
  metaConnectStep?: MetaConnectStep | null;
  facebookAccessToken: string | null;
  inFlight: InviteInFlightFlags;
  platformErrors: {
    facebook: string | null;
    youtube: string | null;
    tiktok: string | null;
    spotify: string | null;
  };
  integrationSource?: InviteIntegrationSource;
}

export function isOAuthReturn(urlParams: InviteUrlParams): boolean {
  return !!(
    urlParams.successParam ||
    urlParams.disconnectedParam ||
    urlParams.errorParam ||
    urlParams.verifiedParam
  );
}

export function isPlatformSuccess(urlParams: InviteUrlParams): boolean {
  return !!urlParams.successParam;
}

export function isPlatformDisconnected(urlParams: InviteUrlParams): boolean {
  return !!urlParams.disconnectedParam;
}

export function hasPlatformErrors(errors: DeriveInviteViewPhaseInput["platformErrors"]): boolean {
  return !!(errors.facebook || errors.youtube || errors.tiktok || errors.spotify);
}
